"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useSubmitWorkflow = void 0;
const react_1 = require("react");
const react_i18next_1 = require("react-i18next");
const luxon_1 = require("luxon");
const hooks_1 = require("../../../hooks");
const ToastIcon_1 = require("../../../components/Shared/ToastIcon");
const consts_1 = require("../../../consts");
const PunchWorkflowTypes_1 = require("../PunchWorkflowTypes");
const useSubmitWorkflow = ({ associateInfo, featureGates = {}, action, inEditPunch, setIsShowSummary }) => {
    const { t } = (0, react_i18next_1.useTranslation)();
    const { PUNCH_WORKFLOW_FEATURE } = consts_1.featureNames;
    const { ctkPolicy, isSubmitMissedPunchV2Enabled, isPunchCorrStatusEnabled } = featureGates;
    const [isSubmitting, setIsSubmitting] = (0, react_1.useState)(false);
    const [status, setStatus] = (0, react_1.useState)();
    const [title, setTitle] = (0, react_1.useState)('');
    const [supportingText, setSupportingText] = (0, react_1.useState)('');
    const [detailsTitle, setDetailsTitle] = (0, react_1.useState)();
    const [detailsSubText, setDetailsSubText] = (0, react_1.useState)();
    const [detailsNote, setDetailsNote] = (0, react_1.useState)();
    const { submitPunchState: { loading: submitPunchLoading, data: submitPunchTime, error: submitPunchError }, submitPunch } = (0, hooks_1.useSubmitPunch)(Object.assign(Object.assign({}, associateInfo), { ctkPolicy: ctkPolicy, usageLocation: PUNCH_WORKFLOW_FEATURE, useV2Api: isSubmitMissedPunchV2Enabled }));
    const { editPunchState: { loading: editPunchLoading, data: editPunchData, error: editPunchError }, editPunch } = (0, hooks_1.useEditPunch)(Object.assign(Object.assign({}, associateInfo), { usageLocation: PUNCH_WORKFLOW_FEATURE }));
    const { deletePunchState: { loading: deletePunchLoading, data: deletePunchTime, error: deletePunchError }, deletePunch } = (0, hooks_1.useDeletePunch)(Object.assign(Object.assign({}, associateInfo), { useV2Api: isPunchCorrStatusEnabled, usageLocation: PUNCH_WORKFLOW_FEATURE }));
    (0, react_1.useEffect)(() => {
        setIsSubmitting(submitPunchLoading || editPunchLoading || deletePunchLoading);
    }, [submitPunchLoading, editPunchLoading, deletePunchLoading]);
    (0, react_1.useEffect)(() => {
        if (action !== PunchWorkflowTypes_1.PUNCH_WORKFLOW_ACTIONS.ADD || isSubmitting) {
            return;
        }
        let addTitle = '';
        let addSupportingText = '';
        let status = undefined;
        if (submitPunchTime) {
            status = ToastIcon_1.ToastIconStatus.success;
            addTitle = t('time_and_attendance_PunchToastModal_MissedPunch_Title');
            const punchDateTime = luxon_1.DateTime.fromISO(submitPunchTime);
            addSupportingText = t('time_and_attendance_PunchToastModal_MissedPunch_Text', {
                punchDate: punchDateTime.toLocaleString({
                    month: 'long',
                    day: 'numeric'
                }),
                punchTime: punchDateTime.toLocaleString(luxon_1.DateTime.TIME_SIMPLE)
            });
        }
        if (submitPunchError) {
            status = ToastIcon_1.ToastIconStatus.error;
            addTitle = t('time_and_attendance_PunchErrorModal_Title');
            addSupportingText = t('time_and_attendance_PunchErrorModal_Content');
        }
        if (submitPunchTime || submitPunchError) {
            setStatus(status);
            setTitle(addTitle);
            setSupportingText(addSupportingText);
            setIsShowSummary(true);
        }
    }, [t, isSubmitting, action, submitPunchTime, submitPunchError]);
    (0, react_1.useEffect)(() => {
        if (action !== PunchWorkflowTypes_1.PUNCH_WORKFLOW_ACTIONS.EDIT || isSubmitting) {
            return;
        }
        let status = undefined;
        let editTitle = '';
        let editSupportingText = '';
        let editDetailsTitle = undefined;
        let editDetailsText = undefined;
        let editDetailsNote = undefined;
        if (editPunchData) {
            status = ToastIcon_1.ToastIconStatus.success;
            editTitle = t('time_and_attendance_Success');
            editSupportingText = t('time_and_attendance_PunchToastModal_PunchEdit_Success_Text');
            editDetailsNote = t('time_and_attendance_PunchToastModal_PunchEdit_Note_Text');
        }
        if (editPunchError) {
            status = ToastIcon_1.ToastIconStatus.error;
            editTitle = t('time_and_attendance_system_error');
            editSupportingText = t('time_and_attendance_PunchToastModal_PunchEdit_Error_Text');
            if (editPunchError.statusCode === 400 &&
                editPunchError.message.includes('MealLockout')) {
                editTitle = t('time_and_attendance_PunchStatusModal_NotAcceptedTitle');
                editSupportingText = t('time_and_attendance_PunchModal_PunchEditError_MealBreakLockout');
                editDetailsTitle = t('time_and_attendance_PunchCorrectionType_EditedPunch');
                const punchDate = luxon_1.DateTime.fromISO(inEditPunch);
                const month = t(`time_and_attendance_PunchCard_daySelector_${punchDate.month}`);
                const timeString = punchDate.toLocaleString(luxon_1.DateTime.TIME_SIMPLE);
                const day = punchDate.day;
                const year = punchDate.year;
                editDetailsText = `${timeString} | ${month} ${day}, ${year}`;
            }
        }
        if (editPunchData || editPunchError) {
            setStatus(status);
            setTitle(editTitle);
            setSupportingText(editSupportingText);
            setDetailsTitle(editDetailsTitle);
            setDetailsSubText(editDetailsText);
            setDetailsNote(editDetailsNote);
            setIsShowSummary(true);
        }
    }, [t, isSubmitting, action, inEditPunch, editPunchData, editPunchError]);
    (0, react_1.useEffect)(() => {
        if (action !== PunchWorkflowTypes_1.PUNCH_WORKFLOW_ACTIONS.DELETE || isSubmitting) {
            return;
        }
        let status = undefined;
        let deleteTitle = '';
        let deleteSupportingText = '';
        if (deletePunchTime) {
            status = ToastIcon_1.ToastIconStatus.success;
            deleteTitle = t('time_and_attendance_PunchToastModal_PunchDeleted');
            const punchDateTime = luxon_1.DateTime.fromISO(deletePunchTime);
            deleteSupportingText = t('time_and_attendance_PunchToastModal_DeletePunch_Text', {
                punchDate: punchDateTime.toLocaleString({
                    month: 'long',
                    day: 'numeric'
                }),
                punchTime: punchDateTime.toLocaleString(luxon_1.DateTime.TIME_SIMPLE)
            });
        }
        if (deletePunchError) {
            status = ToastIcon_1.ToastIconStatus.error;
            deleteTitle = t('time_and_attendance_PunchToastModal_Error');
            deleteSupportingText = t('time_and_attendance_PunchToastModal_DeletePunch_GeneralErrorText');
            if (deletePunchError.statusCode === 404) {
                deleteSupportingText = t('time_and_attendance_PunchToastModal_DeletePunch_DuplicateDeletionErrorText');
            }
        }
        if (deletePunchTime || deletePunchError) {
            setStatus(status);
            setTitle(deleteTitle);
            setSupportingText(deleteSupportingText);
            setIsShowSummary(true);
        }
    }, [t, isSubmitting, action, deletePunchTime, deletePunchError]);
    return {
        setIsSubmitting,
        isSubmitting,
        submitPunch,
        deletePunch,
        editPunch,
        status,
        title,
        supportingText,
        detailsTitle,
        detailsSubText,
        detailsNote
    };
};
exports.useSubmitWorkflow = useSubmitWorkflow;
